import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableHead, TableRow, Paper,
  TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, InputLabel, FormControl,
  Checkbox, TablePagination, Tooltip, CircularProgress, Snackbar, Alert
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { Add, Edit, Delete, CheckCircle, Block, FileDownload } from '@mui/icons-material';

// Mock user data type
interface User {
  id: string;
  name: string;
  email: string;
  role: 'user' | 'admin' | 'superadmin';
  status: 'active' | 'inactive';
  createdAt: string;
}

// Mock API functions (replace with real API calls)
const mockFetchUsers = async (): Promise<User[]> => {
  await new Promise(r => setTimeout(r, 500));
  return [
    { id: '1', name: 'Alice', email: 'alice@example.com', role: 'admin', status: 'active', createdAt: '2023-01-01' },
    { id: '2', name: 'Bob', email: 'bob@example.com', role: 'user', status: 'inactive', createdAt: '2023-02-01' },
    { id: '3', name: 'Charlie', email: 'charlie@example.com', role: 'user', status: 'active', createdAt: '2023-03-01' },
    { id: '4', name: 'Dana', email: 'dana@example.com', role: 'superadmin', status: 'active', createdAt: '2023-04-01' },
  ];
};

const roles = ['user', 'admin', 'superadmin'] as const;
const statuses = ['active', 'inactive'] as const;

const ManageUsers: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [addMode, setAddMode] = useState(false);
  const [deleteUser, setDeleteUser] = useState<User | null>(null);
  const [bulkSelection, setBulkSelection] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success'|'error'|'info'}>({open: false, message: '', severity: 'info'});

  useEffect(() => {
    setLoading(true);
    mockFetchUsers()
      .then(setUsers)
      .finally(() => setLoading(false));
  }, []);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value);
  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  // Pagination
  const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // User CRUD
  const handleEdit = (user: User) => { setSelectedUser(user); setEditMode(true); };
  const handleAdd = () => { setSelectedUser(null); setAddMode(true); };
  const handleDialogClose = () => { setEditMode(false); setAddMode(false); setSelectedUser(null); };
  const handleDelete = (user: User) => setDeleteUser(user);
  const confirmDelete = () => {
    setUsers(users.filter(u => u.id !== (deleteUser && deleteUser.id)));
    setDeleteUser(null);
    setSnackbar({open: true, message: 'User deleted', severity: 'success'});
  };
  const handleStatusToggle = (user: User) => {
    setUsers(users.map(u => u.id === user.id ? { ...u, status: u.status === 'active' ? 'inactive' : 'active' } : u));
    setSnackbar({open: true, message: `User ${user.status === 'active' ? 'deactivated' : 'activated'}`, severity: 'info'});
  };
  const handleRoleChange = (user: User, role: User['role']) => {
    setUsers(users.map(u => u.id === user.id ? { ...u, role } : u));
    setSnackbar({open: true, message: 'Role updated', severity: 'success'});
  };
  const handleBulkSelect = (id: string) => {
    setBulkSelection(sel => sel.includes(id) ? sel.filter(i => i !== id) : [...sel, id]);
  };
  const handleBulkDelete = () => {
    setUsers(users.filter(u => !bulkSelection.includes(u.id)));
    setBulkSelection([]);
    setSnackbar({open: true, message: 'Users deleted', severity: 'success'});
  };
  const handleBulkStatus = (status: User['status']) => {
    setUsers(users.map(u => bulkSelection.includes(u.id) ? { ...u, status } : u));
    setSnackbar({open: true, message: `Users set to ${status}`, severity: 'success'});
  };

  const handleExport = () => {
    const csv = [
      ['ID', 'Name', 'Email', 'Role', 'Status', 'Created At'],
      ...users.map(u => [u.id, u.name, u.email, u.role, u.status, u.createdAt])
    ].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'users.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Add/Edit dialog handlers
  const [form, setForm] = useState<Partial<User>>({});
  useEffect(() => {
    if (editMode && selectedUser) setForm(selectedUser);
    else if (addMode) setForm({ name: '', email: '', role: 'user', status: 'active', createdAt: new Date().toISOString().slice(0,10) });
    else setForm({});
  }, [editMode, addMode, selectedUser]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name as string]: value }));
  };
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name as string]: value }));
  };
  const handleFormSubmit = () => {
    if (!form.name || !form.email) {
      setSnackbar({open: true, message: 'Name and email are required', severity: 'error'});
      return;
    }
    if (editMode && selectedUser) {
      setUsers(users.map(u => u.id === selectedUser.id ? { ...u, ...form } as User : u));
      setSnackbar({open: true, message: 'User updated', severity: 'success'});
    } else if (addMode) {
      setUsers([{ ...form, id: Math.random().toString(), createdAt: form.createdAt || new Date().toISOString().slice(0,10) } as User, ...users]);
      setSnackbar({open: true, message: 'User added', severity: 'success'});
    }
    handleDialogClose();
  };

  return (
    <Box sx={{ p: { xs: 1, md: 4 } }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>User Management</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <TextField size="small" placeholder="Search by name or email" value={search} onChange={handleSearch} sx={{ width: { xs: '100%', md: '30%' } }} />
        <Box>
          <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleAdd} sx={{ mr: 1 }}>Add User</Button>
          <Button variant="outlined" color="error" startIcon={<Delete />} onClick={handleBulkDelete} disabled={!bulkSelection.length} sx={{ mr: 1 }}>Delete Selected</Button>
          <Button variant="outlined" color="secondary" startIcon={<Block />} onClick={() => handleBulkStatus('inactive')} disabled={!bulkSelection.length} sx={{ mr: 1 }}>Deactivate</Button>
          <Button variant="outlined" color="success" startIcon={<CheckCircle />} onClick={() => handleBulkStatus('active')} disabled={!bulkSelection.length} sx={{ mr: 1 }}>Activate</Button>
          <Button variant="outlined" color="info" startIcon={<FileDownload />} onClick={handleExport}>Export CSV</Button>
        </Box>
      </Box>
      <Paper sx={{ width: '100%', overflow: 'auto', mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={bulkSelection.length > 0 && bulkSelection.length < paginatedUsers.length}
                  checked={paginatedUsers.length > 0 && bulkSelection.length === paginatedUsers.length}
                  onChange={(_, checked) => setBulkSelection(checked ? paginatedUsers.map(u => u.id) : [])}
                  inputProps={{ 'aria-label': 'select all users' }}
                />
              </TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7}><Box sx={{ display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box></TableCell></TableRow>
            ) : paginatedUsers.length === 0 ? (
              <TableRow><TableCell colSpan={7}><Typography align="center">No users found.</Typography></TableCell></TableRow>
            ) : (
              paginatedUsers.map(user => (
                <TableRow key={user.id} hover selected={bulkSelection.includes(user.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox checked={bulkSelection.includes(user.id)} onChange={() => handleBulkSelect(user.id)} />
                  </TableCell>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Select
                      value={user.role}
                      onChange={e => handleRoleChange(user, e.target.value as User['role'])}
                      size="small"
                    >
                      {roles.map(role => (
                        <MenuItem value={role} key={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant={user.status === 'active' ? 'outlined' : 'contained'}
                      color={user.status === 'active' ? 'success' : 'error'}
                      startIcon={user.status === 'active' ? <CheckCircle /> : <Block />}
                      onClick={() => handleStatusToggle(user)}
                    >
                      {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                    </Button>
                  </TableCell>
                  <TableCell>{user.createdAt}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton color="primary" size="small" onClick={() => handleEdit(user)}><Edit /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton color="error" size="small" onClick={() => handleDelete(user)}><Delete /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredUsers.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      {/* Add/Edit User Dialog */}
      <Dialog open={editMode || addMode} onClose={handleDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>{editMode ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Name"
            name="name"
            value={form.name || ''}
            onChange={handleInputChange}
            fullWidth
          />
          <TextField
            margin="dense"
            label="Email"
            name="email"
            value={form.email || ''}
            onChange={handleInputChange}
            fullWidth
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Role</InputLabel>
            <Select
              name="role"
              value={form.role || 'user'}
              onChange={handleSelectChange}
              label="Role"
            >
              {roles.map(role => (
                <MenuItem value={role} key={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={form.status || 'active'}
              onChange={handleSelectChange}
              label="Status"
            >
              {statuses.map(status => (
                <MenuItem value={status} key={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleFormSubmit} variant="contained" color="primary">{editMode ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteUser} onClose={() => setDeleteUser(null)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete user {deleteUser?.name}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUser(null)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({...s, open: false}))}>
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageUsers;
